/**
 * "My estimates" in the top bar (JF 2026-10-16): the estimates the server is running for me — closing the browser does
 * not stop them. How many are running / queued / waiting for my answers; open, continue, stop or retry each one.
 */
import { useEffect, useRef, useState } from "react";
import { api, type Job, type JobList } from "../../shared/api.ts";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { localTime } from "../../shared/time.ts";
import { okToReplace } from "../../shared/unsaved.ts";
import { useGroup } from "../group/groupStore.ts";
import { cancelEstimate, JOB_UPDATED } from "./jobActions.ts";

const ACTIVE = ["queued", "extract", "price", "save"];

export function MyJobs() {
  const t = useT();
  const { go, toast, setSheet } = useStore.getState();
  const [list, setList] = useState<JobList | null>(null);
  const [open, setOpen] = useState(false);
  const [stopping, setStopping] = useState<Set<string>>(new Set());
  const loadVersion = useRef(0);
  const seen = useRef(new Map<string, string>()); // job → last status, to say when one finishes
  const box = useRef<HTMLDivElement>(null);

  async function load() {
    const version = ++loadVersion.current;
    const L = await api.jobs().catch(() => null);
    if (!L || version !== loadVersion.current) return;
    for (const j of L.items) {
      const was = seen.current.get(j.id);
      if (was && ACTIVE.includes(was) && j.status === "done") toast(t(`Estimated and saved: ${j.title || j.customer}`, `已估完并保存：${j.title || j.customer}`));
      if (was && ACTIVE.includes(was) && j.status === "answer") toast(t(`Waiting for your answers: ${j.title}`, `等你回答：${j.title}`));
      seen.current.set(j.id, j.status);
    }
    setList(L);
  }
  // every 3 s while something runs, else every 20 s (and when the window comes back)
  const busy = !!list && list.running + list.queued > 0;
  useEffect(() => {
    load();
    const h = window.setInterval(load, busy || open ? 3000 : 20000);
    const f = () => load();
    window.addEventListener("focus", f);
    window.addEventListener(JOB_UPDATED, f);
    return () => { window.clearInterval(h); window.removeEventListener("focus", f); window.removeEventListener(JOB_UPDATED, f); };
  }, [busy, open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!list || !list.items.length) return null;
  const label = (j: Job) => ({
    queued: t("queued", "排队中"), extract: t("reading", "读取中"), answer: t("waiting for you", "等你回答"), price: t("pricing", "定价中"),
    save: t("saving", "保存中"), done: t("done", "已完成"), err: t("failed", "失败"), cancelled: t("stopped", "已停止"),
  })[j.status];
  async function openSheet(j: Job) {
    if (!j.sheetId || !okToReplace()) return;
    const r = await api.sheet(j.sheetId);
    setSheet(r.data, { id: r.id }); useStore.getState().markSaved(r.data); setOpen(false); go("sheet");
  }
  async function act(fn: () => Promise<unknown>) { try { await fn(); } catch (e) { toast(e instanceof Error ? e.message : String(e)); } load(); }
  async function stop(j: Job) {
    if (stopping.has(j.id) || !confirm(t("Stop this estimate?", "停止这一款？"))) return;
    setStopping(s => new Set(s).add(j.id));
    try {
      const result = await cancelEstimate(j.id);
      toast(result.status === "cancelled" ? t("Estimate stopped", "估价已停止") : result.status === "done" ? t("Already completed — the result is kept", "任务已完成，已保留结果") : t("The estimate has already ended", "任务已结束"));
    } catch (e) { toast(e instanceof Error ? e.message : String(e)); }
    finally { await load(); setStopping(s => { const next = new Set(s); next.delete(j.id); return next; }); }
  }
  const n = list.running + list.queued;
  return (
    <div className="myjobs" ref={box}>
      <button className={"btn sm" + (n || list.answer ? " on" : "")} id="myJobsBtn" onClick={() => setOpen((o) => !o)}
        title={t(`I may run ${list.limits.perUser} at a time`, `每人最多同时跑 ${list.limits.perUser} 款`)}>
        {n > 0 && <i className="spin" />}
        {t("My estimates", "我的估价")}
        {list.running > 0 && <b data-jobs="running"> · {t(`${list.running} running`, `进行中 ${list.running}`)}</b>}
        {list.queued > 0 && <span data-jobs="queued"> · {t(`${list.queued} queued`, `排队 ${list.queued}`)}</span>}
        {list.answer > 0 && <span data-jobs="answer" className="warn"> · {t(`${list.answer} need you`, `待回答 ${list.answer}`)}</span>}
      </button>
      {open && (
        <div className="myjobs-pop" id="myJobs">
          <div className="dim" style={{ fontSize: 12, marginBottom: 6 }}>{t("Run on the server — closing the browser does not stop them. Last 24 hours.", "在服务器上跑——关浏览器不会停。显示最近 24 小时。")}</div>
          <table className="t"><tbody>
            {list.items.map((j) => (
              <tr key={j.id} data-job={j.id} data-job-status={j.status}>
                <td style={{ maxWidth: 260 }}>{j.title || j.customer || "—"}{j.kind === "batch" && <span className="dim"> · {t("batch", "批量")}</span>}
                  {j.status === "err" && j.error && <div className="dim" style={{ fontSize: 11.5 }}>{j.error.slice(0, 160)}</div>}</td>
                <td style={{ whiteSpace: "nowrap" }}>{ACTIVE.includes(j.status) && <i className="spin" />}{label(j)}</td>
                <td className="dim mono" style={{ whiteSpace: "nowrap", fontSize: 11.5 }}>{localTime(j.finishedAt || j.startedAt || j.createdAt)}</td>
                <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  {j.status === "done" && j.sheetId && <button className="btn sm p" data-job-open onClick={() => openSheet(j)}>{t("Open", "打开")}</button>}
                  {j.status === "answer" && <button className="btn sm p" data-job-resume disabled={stopping.has(j.id)} onClick={() => { if (!okToReplace()) return; useStore.getState().setResumeJob(j.id); setOpen(false); go("ai"); }}>{t("Continue", "继续")}</button>}
                  {(ACTIVE.includes(j.status) || j.status === "answer") && <button className="btn sm" data-job-stop disabled={stopping.has(j.id)} onClick={() => stop(j)}>{stopping.has(j.id) ? t("Stopping…", "正在停止…") : t("Stop", "停止")}</button>}
                  {(j.status === "err" || j.status === "cancelled") && <button className="btn sm" data-job-retry onClick={() => act(() => api.jobRetry(j.id))}>{t("Retry", "重试")}</button>}
                  {j.batchId && j.status === "done" && <BatchGroupLink batchId={j.batchId} onOpen={() => setOpen(false)} />}
                </td>
              </tr>))}
          </tbody></table>
        </div>)}
    </div>
  );
}

/** a finished batch: its style group */
function BatchGroupLink({ batchId, onOpen }: { batchId: string; onOpen(): void }) {
  const t = useT();
  const [g, setG] = useState<string | null>(null);
  useEffect(() => { api.jobs(batchId).then((r) => setG(r.groupId)).catch(() => undefined); }, [batchId]);
  if (!g) return null;
  return <span className="lnk" style={{ marginLeft: 8, fontSize: 12 }} onClick={async () => { await useGroup.getState().open(g); onOpen(); useStore.getState().go("group"); }}>{t("group", "组合")}</span>;
}
